'use client';

import Script from 'next/script';
import { useEffect, useRef } from 'react';

interface TurnstileWidgetProps {
  /** Cloudflare Turnstile site key (NEXT_PUBLIC_TURNSTILE_SITE_KEY). */
  siteKey: string;
  /** Fires with the token when the challenge passes; '' on expiry/error. */
  onToken: (token: string) => void;
  /** Visual theme; matches the rest of the public surface. */
  theme?: 'light' | 'dark' | 'auto';
}

/**
 * Cloudflare Turnstile challenge widget. Loads the script via next/script,
 * renders into a div, and emits tokens up via onToken. Re-renders the
 * challenge when the parent passes a different siteKey (rare). Cleans up
 * the iframe on unmount.
 *
 * Caller is responsible for sending the token to /api/dealer-inquiry (or
 * wherever it's verified). The widget itself doesn't network at all
 * beyond what Cloudflare's script handles internally.
 */
export function TurnstileWidget({ siteKey, onToken, theme = 'light' }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    function tryRender() {
      const w = window as unknown as {
        turnstile?: {
          render: (
            el: HTMLElement,
            opts: {
              sitekey: string;
              callback: (token: string) => void;
              'expired-callback'?: () => void;
              'error-callback'?: () => void;
              theme?: string;
            },
          ) => string;
          remove: (id: string) => void;
        };
      };
      if (!w.turnstile || !containerRef.current) return false;
      widgetIdRef.current = w.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onToken(token),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
        theme,
      });
      return true;
    }
    if (tryRender()) {
      return () => {
        const w = window as unknown as { turnstile?: { remove: (id: string) => void } };
        if (widgetIdRef.current && w.turnstile) {
          w.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }
    // Script may not be loaded yet. Poll briefly.
    const interval = window.setInterval(() => {
      if (tryRender()) window.clearInterval(interval);
    }, 100);
    return () => {
      window.clearInterval(interval);
      const w = window as unknown as { turnstile?: { remove: (id: string) => void } };
      if (widgetIdRef.current && w.turnstile) {
        w.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken, theme]);

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        async
        defer
        strategy="afterInteractive"
      />
      <div ref={containerRef} />
    </>
  );
}
